import { Plus, Shield } from "lucide-react";
import { useState } from "react";
import { PERMISSIONS } from "@/auth/permissions";
import { DeleteRoleModal } from "@/components/admin/DeleteRoleModal";
import { RoleDrawer } from "@/components/admin/RoleDrawer";
import { RolesList } from "@/components/admin/RolesList";
import { Button, EmptyState, Spinner } from "@/components/common";
import { PageHeader } from "@/components/layout/PageHeader";
import { usePermissions, useRoles } from "@/hooks/useAdmin";
import { useHasPermission } from "@/hooks/usePermission";
import type { Role } from "@/types/rbac";

type DrawerMode =
  | { type: "create" }
  | { type: "edit"; role: Role }
  | { type: "view"; role: Role };

export function RolesPage() {
  const { data: roles = [], isLoading: rolesLoading } = useRoles();
  const { data: permissionGroups = [], isLoading: permsLoading } =
    usePermissions();

  const canCreate = useHasPermission(PERMISSIONS.ROLES.CREATE);
  const canUpdate = useHasPermission(PERMISSIONS.ROLES.UPDATE);
  const canDelete = useHasPermission(PERMISSIONS.ROLES.DELETE);

  const [drawerMode, setDrawerMode] = useState<DrawerMode | null>(null);
  const [deleteRole, setDeleteRole] = useState<Role | null>(null);

  const isLoading = rolesLoading || permsLoading;

  function handleEdit(role: Role) {
    if (role.isSystem) {
      setDrawerMode({ type: "view", role });
    } else {
      setDrawerMode({ type: "edit", role });
    }
  }

  if (isLoading) {
    return (
      <div className="animate-fade-in">
        <PageHeader
          title="Role i uprawnienia"
          description="Definiuj role i przypisuj granularne uprawnienia do zasobów systemu"
        />
        <div className="flex justify-center py-24">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Role i uprawnienia"
        description="Definiuj role i przypisuj granularne uprawnienia do zasobów systemu"
        actions={
          canCreate ? (
            <Button
              leftIcon={<Plus size={16} />}
              onClick={() => {
                setDrawerMode({ type: "create" });
              }}
            >
              Dodaj rolę
            </Button>
          ) : undefined
        }
      />

      {roles.length > 0 ? (
        <RolesList
          roles={roles}
          permissionGroups={permissionGroups}
          onEdit={handleEdit}
          onDelete={setDeleteRole}
          canUpdate={canUpdate}
          canDelete={canDelete}
        />
      ) : (
        <EmptyState
          icon={<Shield size={32} />}
          title="Brak ról"
          description="Nie utworzono jeszcze żadnych ról. Dodaj pierwszą rolę."
          action={
            canCreate ? (
              <Button
                leftIcon={<Plus size={16} />}
                onClick={() => {
                  setDrawerMode({ type: "create" });
                }}
              >
                Dodaj rolę
              </Button>
            ) : undefined
          }
        />
      )}

      <RoleDrawer
        mode={drawerMode}
        onClose={() => {
          setDrawerMode(null);
        }}
      />

      <DeleteRoleModal
        role={deleteRole}
        onClose={() => {
          setDeleteRole(null);
        }}
      />
    </div>
  );
}
